import React, {
  HTMLAttributes,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Element, Navbar } from 'react-bulma-components';
import { RenderAsComponent } from 'react-bulma-components/src/components';
import If from '../../shared/if/if';
import AnimatedChevronIcon from '../animated-chevron-icon/animated-chevron-icon';
import { onDropdownMenuKey } from './dropdown-menu-keys';
import styles from './dropdown-menu.module.scss';

export interface DropdownMenuProps {
  children: ReactNode;
  label: ReactNode;
  showArrow?: boolean;
  right?: boolean;
}

type DropdownMenuContextType = {
  close: () => void;
};

const DropdownMenuContext = createContext<DropdownMenuContextType>({
  close: () => {},
});

export function DropdownMenu({
  children,
  label: _label,
  showArrow = true,
  right = true,
}: DropdownMenuProps) {
  const [active, setActive] = useState(false);
  const dropdownRef = useRef<RenderAsComponent>(null);

  const handleDropdownOnClick: React.MouseEventHandler<HTMLAnchorElement> =
    useCallback((e) => {
      e.stopPropagation();
      setActive((prev) => !prev);
    }, []);

  const handleDropdownOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleDropdownOnClick(
          e as unknown as React.MouseEvent<HTMLAnchorElement>
        );
        return;
      }
      onDropdownMenuKey(active, setActive)(e);
    },
    [active, handleDropdownOnClick]
  );

  const handleDropdownOnBlur = useCallback(
    (e: React.FocusEvent<HTMLAnchorElement>) => {
      if (
        e.relatedTarget instanceof HTMLElement &&
        !e.currentTarget.contains(e.relatedTarget)
      ) {
        setActive(false);
      }
    },
    []
  );

  const close = useCallback(() => {
    setActive(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as unknown as HTMLElement).contains(
          event.target as Node
        )
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [close]);

  const label = useMemo(() => {
    if (showArrow) {
      return (
        <Element display="flex" alignItems="center" className="gap-2">
          {_label}
          <AnimatedChevronIcon
            className="has-text-primary-dark"
            active={active}
          />
        </Element>
      );
    }
    return _label;
  }, [_label, active, showArrow]);

  const labelClassName = useMemo(() => {
    let className = styles.label;

    if (active) {
      className += ` ${styles.active}`;
    }

    return className;
  }, [active]);

  return (
    <DropdownMenuContext.Provider value={{ close }}>
      <Navbar.Item
        active={active}
        onClick={handleDropdownOnClick}
        onKeyDown={handleDropdownOnKeyDown}
        onBlur={handleDropdownOnBlur}
        className={styles.container}
        domRef={dropdownRef}
      >
        <Navbar.Link
          paddingless
          arrowless
          className={labelClassName}
          role="button"
          aria-expanded={active}
          tabIndex={0}
        >
          {label}
        </Navbar.Link>
        <Navbar.Dropdown className="is-borderless" right={right} role="menu">
          {children}
        </Navbar.Dropdown>
      </Navbar.Item>
    </DropdownMenuContext.Provider>
  );
}

type ItemProps = HTMLAttributes<HTMLAnchorElement> & {
  closeOnClick?: boolean;
  stopEventPropagationOnClick?: boolean;
  children: ReactNode;
  show?: boolean;
};

const Item = ({
  onClick,
  closeOnClick = true,
  stopEventPropagationOnClick = true,
  children,
  show = true,
}: ItemProps) => {
  const { close } = useContext(DropdownMenuContext);

  const handleOnClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (stopEventPropagationOnClick) {
      e.stopPropagation();
    }

    onClick?.(e);

    if (closeOnClick) {
      close();
    }
  };

  // Enter/Space would otherwise reach the dropdown's handler and toggle it, so
  // the item handles them itself, as a click. Escape still closes the menu.
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.click();
    }
  };

  return (
    <If condition={show}>
      <Navbar.Item
        onClick={handleOnClick}
        onKeyDown={handleOnKeyDown}
        tabIndex={0}
        role="menuitem"
      >
        {children}
      </Navbar.Item>
    </If>
  );
};

DropdownMenu.Item = Item;

export default DropdownMenu;
